/**
 * OTA Firmware Metadata Parser and Validator
 * 
 * CM7/CM4 - Metadatos embebidos al final del binario (76 bytes):
 * - Offset 0-3: Número mágico (4 bytes)
 * - Offset 4-15: Nombre de proyecto (12 bytes)
 * - Offset 16-39: Versión binario (24 bytes)
 * - Offset 40-59: Fecha/hora creación (20 bytes)
 * - Offset 60-69: Versión placa electrónica (10 bytes)
 * - Offset 70-71: ID microcontrolador (2 bytes, Little endian)
 * - Offset 72-75: Número mágico (4 bytes)
 * 
 * ESP - Metadatos al inicio del binario:
 * - Offset 0x00: Magic Word Espressif (0xE9)
 * - Offset 0x0A-0x0B: ChipID
 * - Offset 0x20-0x23: Magic App (0xABCD5432)
 * - Offset 0x30-0x4F: Tag de Git (32 bytes)
 * - Offset 0x50-0x6F: Nombre del Proyecto (32 bytes)
 * - Offset 0x70-0x7F: Hora de Compilación (16 bytes)
 * - Offset 0x80-0x8F: Fecha de Compilación (16 bytes)
 */

import { Buffer } from 'buffer';

export interface FirmwareMetadata {
  magicNumberStart: string;
  projectName: string;
  binaryVersion: string;
  creationDateTime: string;
  boardVersion: string;
  microcontrollerID: string;
  magicNumberEnd: string;
}

export interface EspFirmwareMetadata {
  gitTag: string;
  projectName: string;
  compilationTime: string;
  compilationDate: string;
  chipID: string;
  magicWord: string;
  magicApp: string;
}

export interface MetadataValidationResult {
  valid: boolean;
  error?: string;
  metadata?: FirmwareMetadata | EspFirmwareMetadata;
}

export interface MetadataComparisonResult {
  match: boolean;
  differences: string[];
}

const METADATA_SIZE = 76;
const MAGIC_NUMBER = 'iiii';
const ESP_MAGIC_WORD = 0xE9;
const ESP_MAGIC_APP = 0xABCD5432;
const ESP_MIN_SIZE = 0x90; // Mínimo 144 bytes para contener todos los metadatos

// Offsets de metadatos ESP (fijos según especificación ESP32)
const ESP_GIT_TAG_OFFSET = 0x30;
const ESP_GIT_TAG_END = 0x50;
const ESP_PROJECT_NAME_OFFSET = 0x50;
const ESP_PROJECT_NAME_END = 0x70;
const ESP_COMPILATION_TIME_OFFSET = 0x70;
const ESP_COMPILATION_TIME_END = 0x80;
const ESP_COMPILATION_DATE_OFFSET = 0x80;
const ESP_COMPILATION_DATE_END = 0x90;

/**
 * Decodifica bytes UTF-8 de un Uint8Array
 */
function bytesToString(buffer: Uint8Array, start: number, end: number): string {
  const decoder = new TextDecoder('utf-8');
  return decoder.decode(buffer.slice(start, end));
}

/**
 * Lee un uint16 en formato Little Endian
 */
function readUInt16LE(buffer: Uint8Array, offset: number): number {
  return buffer[offset] | (buffer[offset + 1] << 8);
}

/**
 * Lee un uint32 en formato Little Endian
 */
function readUInt32LE(buffer: Uint8Array, offset: number): number {
  return ((buffer[offset] | (buffer[offset + 1] << 8) | (buffer[offset + 2] << 16) | (buffer[offset + 3] << 24)) >>> 0);
}

/**
 * Detecta si un binario es ESP basándose en el Magic Word
 */
function isEspBinary(buffer: Uint8Array): boolean {
  if (buffer.length < 1) return false;
  return buffer[0] === ESP_MAGIC_WORD;
}

/**
 * Parsea metadatos de binarios ESP
 */
function parseEspOtaMetadata(buffer: Uint8Array): MetadataValidationResult {
  try {
    if (buffer.length < ESP_MIN_SIZE) {
      return {
        valid: false,
        error: `File is too small. ESP firmware needs at least ${ESP_MIN_SIZE} bytes. Got ${buffer.length}.`,
      };
    }

    // Validar Magic Word Espressif
    if (buffer[0] !== ESP_MAGIC_WORD) {
      return {
        valid: false,
        error: `Invalid ESP magic word. Expected 0xE9, got 0x${buffer[0].toString(16).toUpperCase().padStart(2, '0')}.`,
      };
    }

    // Validar Magic App
    const magicApp = readUInt32LE(buffer, 0x20);
    if (magicApp !== ESP_MAGIC_APP) {
      return {
        valid: false,
        error: `Invalid ESP magic app. Expected 0xABCD5432, got 0x${magicApp.toString(16).toUpperCase().padStart(8, '0')}.`,
      };
    }

    // Leer ChipID
    const chipID = readUInt16LE(buffer, 0x0A);
    const chipIDHex = `0x${chipID.toString(16).toUpperCase().padStart(4, '0')}`;

    // Extraer Git Tag (32 bytes from 0x30 to 0x4F)
    const gitTag = bytesToString(buffer, ESP_GIT_TAG_OFFSET, ESP_GIT_TAG_END)
      .replace(/\0/g, '')
      .trim();

    // Extraer Nombre del Proyecto (32 bytes from 0x50 to 0x6F)
    const projectName = bytesToString(buffer, ESP_PROJECT_NAME_OFFSET, ESP_PROJECT_NAME_END)
      .replace(/\0/g, '')
      .trim();

    // Extraer Hora de Compilación (16 bytes from 0x70 to 0x7F)
    const compilationTime = bytesToString(buffer, ESP_COMPILATION_TIME_OFFSET, ESP_COMPILATION_TIME_END)
      .replace(/\0/g, '')
      .trim();

    // Extraer Fecha de Compilación (16 bytes from 0x80 to 0x8F)
    const compilationDate = bytesToString(buffer, ESP_COMPILATION_DATE_OFFSET, ESP_COMPILATION_DATE_END)
      .replace(/\0/g, '')
      .trim();

    // Validaciones
    if (!gitTag) {
      return {
        valid: false,
        error: 'Git tag is empty in ESP metadata.',
      };
    }

    if (!projectName) {
      return {
        valid: false,
        error: 'Project name is empty in ESP metadata.',
      };
    }

    if (!compilationDate) {
      return {
        valid: false,
        error: 'Compilation date is empty in ESP metadata.',
      };
    }

    const metadata: EspFirmwareMetadata = {
      gitTag,
      projectName,
      compilationTime,
      compilationDate,
      chipID: chipIDHex,
      magicWord: `0x${buffer[0].toString(16).toUpperCase().padStart(2, '0')}`,
      magicApp: `0x${magicApp.toString(16).toUpperCase().padStart(8, '0')}`,
    };

    return {
      valid: true,
      metadata,
    };
  } catch (error: any) {
    return {
      valid: false,
      error: `Error parsing ESP metadata: ${error.message}`,
    };
  }
}

/**
 * Extrae y parsea los metadatos del binario (CM7/CM4 o ESP)
 */
export function parseOtaMetadata(fileBuffer: Uint8Array): MetadataValidationResult {
  try {
    // Asegurar que tenemos un Uint8Array
    let buffer: Uint8Array;
    if (fileBuffer instanceof Uint8Array) {
      buffer = fileBuffer;
    } else if (typeof fileBuffer === 'object' && fileBuffer !== null) {
      buffer = new Uint8Array(fileBuffer as any);
    } else {
      return {
        valid: false,
        error: 'Invalid file buffer type',
      };
    }

    // Detectar tipo de binario
    if (isEspBinary(buffer)) {
      return parseEspOtaMetadata(buffer);
    }

    // Si no es ESP, asumir CM7/CM4 (metadatos al final)
    if (buffer.length < METADATA_SIZE) {
      return {
        valid: false,
        error: `File is too small. Minimum size is ${METADATA_SIZE} bytes for CM7/CM4 metadata.`,
      };
    }

    // Extraer los últimos 76 bytes
    const metadataBuffer = buffer.slice(-METADATA_SIZE);

    // Validar números mágicos
    const magicStart = bytesToString(metadataBuffer, 0, 4);
    const magicEnd = bytesToString(metadataBuffer, 72, 76);

    if (magicStart !== MAGIC_NUMBER || magicEnd !== MAGIC_NUMBER) {
      return {
        valid: false,
        error: `Invalid magic numbers. Expected '${MAGIC_NUMBER}' at start and end, got '${magicStart}' and '${magicEnd}'.`,
      };
    }

    // Parsear campos
    const projectName = bytesToString(metadataBuffer, 4, 16)
      .replace(/\0/g, '')
      .trim();

    const binaryVersion = bytesToString(metadataBuffer, 16, 40)
      .replace(/\0/g, '')
      .trim();

    const creationDateTime = bytesToString(metadataBuffer, 40, 60)
      .replace(/\0/g, '')
      .trim();

    const boardVersion = bytesToString(metadataBuffer, 60, 70)
      .replace(/\0/g, '')
      .trim();

    // ID del microcontrolador en Little endian
    const microcontrollerId = readUInt16LE(metadataBuffer, 70);
    const microcontrollerID = `0x${microcontrollerId.toString(16).toUpperCase().padStart(4, '0')}`;

    // Validar que los campos obligatorios no estén vacíos
    if (!projectName) {
      return {
        valid: false,
        error: 'Project name is empty in CM7/CM4 metadata.',
      };
    }

    if (!binaryVersion) {
      return {
        valid: false,
        error: 'Binary version is empty in CM7/CM4 metadata.',
      };
    }

    if (!boardVersion) {
      return {
        valid: false,
        error: 'Board version is empty in CM7/CM4 metadata.',
      };
    }

    const metadata: FirmwareMetadata = {
      magicNumberStart: magicStart,
      projectName,
      binaryVersion,
      creationDateTime,
      boardVersion,
      microcontrollerID,
      magicNumberEnd: magicEnd,
    };

    return {
      valid: true,
      metadata,
    };
  } catch (error: any) {
    return {
      valid: false,
      error: `Error parsing metadata: ${error.message}`,
    };
  }
}

/**
 * Compara metadatos de dos binarios
 * Los campos que deben coincidir: projectName, binaryVersion, boardVersion, microcontrollerID
 */
export function compareMetadata(
  metadata1: FirmwareMetadata,
  metadata2: FirmwareMetadata
): MetadataComparisonResult {
  const differences: string[] = [];

  if (metadata1.projectName !== metadata2.projectName) {
    differences.push(
      `Project name mismatch: '${metadata1.projectName}' vs '${metadata2.projectName}'`
    );
  }

  if (metadata1.binaryVersion !== metadata2.binaryVersion) {
    differences.push(
      `Binary version mismatch: '${metadata1.binaryVersion}' vs '${metadata2.binaryVersion}'`
    );
  }

  if (metadata1.boardVersion !== metadata2.boardVersion) {
    differences.push(
      `Board version mismatch: '${metadata1.boardVersion}' vs '${metadata2.boardVersion}'`
    );
  }

  if (metadata1.microcontrollerID !== metadata2.microcontrollerID) {
    differences.push(
      `Microcontroller ID mismatch: '${metadata1.microcontrollerID}' vs '${metadata2.microcontrollerID}'`
    );
  }

  return {
    match: differences.length === 0,
    differences,
  };
}

/**
 * Valida que la versión ingresada coincida con la versión en los metadatos
 * Compara numéricamente los componentes de versión semántica (major.minor.patch)
 */
export function validateVersionMatch(
  inputVersion: string,
  metadataVersion: string
): { match: boolean; message?: string } {
  // Extrae la versión semántica (X.Y.Z)
  const semverRegex = /(\d+)\.(\d+)\.(\d+)/;

  const inputMatch = inputVersion.match(semverRegex);
  const metadataMatch = metadataVersion.match(semverRegex);

  // Si ambos tienen versión semántica válida
  if (inputMatch && metadataMatch) {
    const inputMajor = parseInt(inputMatch[1], 10);
    const inputMinor = parseInt(inputMatch[2], 10);
    const inputPatch = parseInt(inputMatch[3], 10);

    const metadataMajor = parseInt(metadataMatch[1], 10);
    const metadataMinor = parseInt(metadataMatch[2], 10);
    const metadataPatch = parseInt(metadataMatch[3], 10);

    // Comparar numéricamente
    const versionsMatch =
      inputMajor === metadataMajor &&
      inputMinor === metadataMinor &&
      inputPatch === metadataPatch;

    if (versionsMatch) {
      return {
        match: true,
      };
    } else {
      return {
        match: false,
        message: `Version mismatch: input ${inputMatch[1]}.${inputMatch[2]}.${inputMatch[3]} vs metadata ${metadataMatch[1]}.${metadataMatch[2]}.${metadataMatch[3]}`,
      };
    }
  }

  // Fallback: comparación directa si no hay formato semántico
  if (inputVersion === metadataVersion) {
    return {
      match: true,
    };
  }

  return {
    match: false,
    message: `Version mismatch: input '${inputVersion}' vs metadata '${metadataVersion}'`,
  };
}

/**
 * Detecta si los metadatos son de CM7/CM4
 */
export const isCm7Cm4Metadata = (metadata: any): metadata is FirmwareMetadata => {
  return metadata && 'binaryVersion' in metadata;
};

/**
 * Detecta si los metadatos son de ESP
 */
export const isEspMetadata = (metadata: any): metadata is EspFirmwareMetadata => {
  return metadata && 'gitTag' in metadata;
};

/**
 * Obtiene la versión de cualquier tipo de metadata
 */
export const getVersionFromMetadata = (
  metadata: FirmwareMetadata | EspFirmwareMetadata
): string => {
  if (isCm7Cm4Metadata(metadata)) {
    return metadata.binaryVersion;
  } else if (isEspMetadata(metadata)) {
    return metadata.gitTag;
  }
  return '';
};

/**
 * Remueve los metadatos embebidos del binario
 * - Para CM7/CM4: Remueve los últimos 76 bytes (metadatos al final)
 * - Para ESP: Reemplaza los metadatos (0x30-0x8F) con ceros, manteniendo el tamaño
 * 
 * @param fileBuffer Buffer del archivo binario
 * @returns Buffer sin metadatos
 */
export function removeOtaMetadata(fileBuffer: Uint8Array | Buffer): Buffer {
  // Convertir a Uint8Array si es necesario
  let buffer: Uint8Array;
  if (fileBuffer instanceof Buffer) {
    buffer = new Uint8Array(fileBuffer);
  } else {
    buffer = fileBuffer;
  }

  // Detectar tipo de binario
  if (isEspBinary(buffer)) {
    return Buffer.from(buffer);
  }
  else {
    // Para CM7/CM4: Quitar los últimos 76 bytes (metadatos)
    if (buffer.length < METADATA_SIZE) {
      // Si el archivo es más pequeño que los metadatos, devolver tal cual
      return Buffer.from(buffer);
    }

    return Buffer.from(buffer.slice(0, buffer.length - METADATA_SIZE));
  }
}
